/**
 * i18n Helpers for Ad Display
 * Ensures ads display in the correct language with proper fallbacks
 */

import { localizeLocationValue } from './locationDisplay';

/**
 * Get the display title for an ad based on current language
 * @param {Object} ad - The ad object
 * @param {string} lang - Current language ('ar' or 'en')
 * @returns {Object} { text: string, isTranslated: boolean, originalLang: 'ar'|'en' }
 */
export function getAdTitle(ad, lang = 'ar') {
  if (!ad) return { text: '', isTranslated: false, originalLang: 'ar' };
  
  const isEnglish = lang === 'en' || lang?.startsWith('en');
  
  if (isEnglish) {
    // English mode: prefer English title
    if (ad.title_en) {
      return { text: ad.title_en, isTranslated: false, originalLang: 'en' };
    }
    // Fallback to Arabic
    return { text: ad.title_ar || ad.title || '', isTranslated: true, originalLang: 'ar' };
  } else {
    // Arabic mode: prefer Arabic title
    if (ad.title_ar || ad.title) {
      return { text: ad.title_ar || ad.title, isTranslated: false, originalLang: 'ar' };
    }
    // Fallback to English
    return { text: ad.title_en || '', isTranslated: true, originalLang: 'en' };
  }
}

/**
 * Get the display description for an ad based on current language
 * @param {Object} ad - The ad object
 * @param {string} lang - Current language ('ar' or 'en')
 * @returns {Object} { text: string, isTranslated: boolean, originalLang: 'ar'|'en' }
 */
export function getAdDescription(ad, lang = 'ar') {
  if (!ad) return { text: '', isTranslated: false, originalLang: 'ar' };
  
  const isEnglish = lang === 'en' || lang?.startsWith('en');
  
  if (isEnglish) {
    if (ad.description_en) {
      return { text: ad.description_en, isTranslated: false, originalLang: 'en' };
    }
    return { text: ad.description_ar || ad.description || '', isTranslated: true, originalLang: 'ar' };
  } else {
    if (ad.description_ar || ad.description) {
      return { text: ad.description_ar || ad.description, isTranslated: false, originalLang: 'ar' };
    }
    return { text: ad.description_en || '', isTranslated: true, originalLang: 'en' };
  }
}

/**
 * Localize a city/location name based on language.
 * Delegates to the catalog-wide resolver in utils/locationDisplay (iter351).
 * @param {string} location - Location string (may include city, state)
 * @param {string} lang - Current language ('ar' or 'en')
 * @returns {string}
 */
export function localizeCity(location, lang = 'ar') {
  return localizeLocationValue(location, lang);
}

/**
 * Get the display location for an ad based on current language
 * @param {Object} ad - The ad object
 * @param {string} lang - Current language ('ar' or 'en')
 * @returns {string}
 */
export function getAdLocation(ad, lang = 'ar') {
  if (!ad) return '';

  const isEnglish = lang === 'en' || lang?.startsWith('en');
  const countryCode = ad.country || ad.countryCode || ad.country_code || '';

  // Handle location as an object {city, city_ar, city_en}
  const loc = ad.location;
  if (loc && typeof loc === 'object') {
    if (isEnglish && loc.city_en) return loc.city_en;
    if (!isEnglish && loc.city_ar) return loc.city_ar;
    return localizeLocationValue(loc.city || '', lang, { countryCode });
  }

  // Try to get the raw location value
  let rawLocation = '';
  if (isEnglish) {
    rawLocation = ad.location_en || ad.city_en || ad.location || ad.city || '';
  } else {
    // For Arabic, prefer location_ar if exists, otherwise translate
    if (ad.location_ar || ad.city_ar) {
      return ad.location_ar || ad.city_ar;
    }
    rawLocation = ad.location || ad.city || ad.location_en || ad.city_en || '';
  }

  // Apply localization through the catalog-wide resolver
  return localizeLocationValue(rawLocation, lang, { countryCode });
}

/**
 * Get the display category name based on language
 * @param {string} categoryKey - Category slug/key
 * @param {string} lang - Current language
 * @returns {string}
 */
export function getCategoryName(categoryKey, lang = 'ar') {
  const isEnglish = lang === 'en' || lang?.startsWith('en');
  
  const categories = {
    'electronics': { ar: 'إلكترونيات', en: 'Electronics' },
    'devices': { ar: 'أجهزة', en: 'Devices' },
    'vehicles': { ar: 'مركبات', en: 'Vehicles' },
    'cars': { ar: 'سيارات', en: 'Cars' },
    'real_estate': { ar: 'عقارات', en: 'Real Estate' },
    'jobs': { ar: 'وظائف', en: 'Jobs' },
    'services': { ar: 'خدمات', en: 'Services' },
    'furniture': { ar: 'أثاث', en: 'Furniture' },
    'food_beverage': { ar: 'أغذية ومشروبات', en: 'Food & Beverage' },
    'agriculture': { ar: 'زراعة', en: 'Agriculture' },
    'energy': { ar: 'طاقة', en: 'Energy' },
    'it_equipment_software': { ar: 'تقنية المعلومات', en: 'IT' },
  };
  
  const cat = categories[categoryKey];
  if (cat) {
    return isEnglish ? cat.en : cat.ar;
  }
  
  // Fallback: capitalize the key
  return categoryKey?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '';
}

/**
 * Get crop name based on language
 * @param {string} cropSlug - Crop slug
 * @param {string} lang - Current language
 * @returns {string}
 */
export function getCropName(cropSlug, lang = 'ar') {
  const isEnglish = lang === 'en' || lang?.startsWith('en');
  
  const crops = {
    'sesame': { ar: 'سمسم', en: 'Sesame' },
    'sorghum': { ar: 'ذرة', en: 'Sorghum' },
    'groundnuts': { ar: 'فول سوداني', en: 'Groundnuts' },
    'onions': { ar: 'بصل', en: 'Onions' },
    'cotton': { ar: 'قطن', en: 'Cotton' },
    'wheat': { ar: 'قمح', en: 'Wheat' },
    'millet': { ar: 'دخن', en: 'Millet' },
    'livestock': { ar: 'مواشي', en: 'Livestock' },
    'farm_services': { ar: 'خدمات زراعية', en: 'Farm Services' },
  };
  
  const crop = crops[cropSlug];
  if (crop) {
    return isEnglish ? crop.en : crop.ar;
  }
  
  return cropSlug || '';
}

/**
 * Check if text is primarily Arabic
 * @param {string} text - Text to check
 * @returns {boolean}
 */
export function isArabicText(text) {
  if (!text) return false;
  const arabicPattern = /[\u0600-\u06FF]/;
  return arabicPattern.test(text);
}
